import React, { type FC, useEffect, useRef, useState } from "react";
import { type IconType } from "react-icons";
import {
  LuArrowRightLeft,
  LuChevronRight,
  LuGauge,
  LuGlobe,
  LuReceiptText,
  LuSendHorizontal,
  LuSmartphone,
} from "react-icons/lu";
import { useI18next } from "gatsby-plugin-react-i18next";

import ChatCard from "@components/chat/chat_card";
import ChatCloseButton from "@components/chat/chat_close_button";
import { CHAT_TOPICS } from "@components/chat/chat_helpers";
import ChatRefreshButton from "@components/chat/chat_refresh_button";
import type { CcConversation } from "@utilities/customer-care";
import { cn } from "@utilities/helper";

/** Popular topics icons, keyed by CHAT_TOPICS `icon`. */
const TOPIC_ICONS: Record<string, IconType> = {
  sim: LuSmartphone,
  globe: LuGlobe,
  billing: LuReceiptText,
  transfer: LuArrowRightLeft,
  plan: LuGauge,
};
/**
 * The chat's opening view (Figma "Message - New Chat" / "Has recent chat"):
 * a wash from the top, the greeting, the brand card that starts a message,
 * then Popular topics or the Chat history: every conversation, not Figma's
 * latest three + "View all" (Htet, 2026-10-07). Scrolled past the brand
 * card, the header title reads "Chat history"; refresh and close stay. The
 * Figma team avatars and online dot were removed (2026-10-07): they took
 * too much room next to refresh and close.
 */
const ChatHome: FC<{
  titleId: string;
  conversations: CcConversation[];
  onStart: (topicKey?: string) => void;
  onOpen: (id: string) => void;
  onRefresh: () => Promise<void>;
  onClose: () => void;
  /** The list's scroll position, kept by the widget so back from a chat
   * returns to the same place. */
  scrollMemory: { get: () => number; set: (value: number) => void };
  /** A conversation is being created: the starters are dimmed and inert. */
  isStarting?: boolean;
}> = ({
  titleId,
  conversations,
  onStart,
  onOpen,
  onRefresh,
  onClose,
  scrollMemory,
  isStarting = false,
}) => {
  const { t } = useI18next();
  const scrollRef = useRef<HTMLDivElement>(null);
  const historyRef = useRef<HTMLHeadingElement>(null);
  const hasHistory = conversations.length > 0;

  useEffect(() => {
    if (scrollRef.current !== null) {
      scrollRef.current.scrollTop = scrollMemory.get();
    }
    // Mount only: the position to come back to.
  }, []);

  // The Chat history heading scrolled up under the header: the header title
  // takes its place.
  const [isPastCard, setPastCard] = useState(false);
  useEffect(() => {
    const root = scrollRef.current;
    const heading = historyRef.current;
    if (root === null || heading === null) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setPastCard(
          !entry.isIntersecting &&
            entry.boundingClientRect.top < (entry.rootBounds?.top ?? 0),
        );
      },
      { root },
    );
    observer.observe(heading);
    return () => {
      observer.disconnect();
    };
  }, [hasHistory]);
  const isHistoryTitle = hasHistory && isPastCard;
  // A slow cross-fade with a short slide: the big title lifts away as the
  // small one (the list's own heading size) rises into place. Reduced
  // motion keeps the cross-fade without the slide.
  const swap =
    "col-start-1 row-start-1 transition duration-300 ease-out motion-reduce:translate-y-0 motion-reduce:transition-opacity";

  return (
    <>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-[402px] bg-gradient-to-b from-primary/10 to-primary/0"
      />

      {/* Scrolled: the header fills with the wash's top colour and a fade
          hangs below it, so the cards melt into the header instead of being
          cut at its edge (no divider line). */}
      <div
        className={cn(
          // The panel colour plus the wash's tint, as two layers.
          "relative z-10 flex shrink-0 items-center gap-2 pb-3 pe-5 ps-4 pt-5 transition-colors duration-300 ease-out before:absolute before:inset-0 before:-z-10 before:bg-primary/10 before:transition-opacity before:duration-300",
          isHistoryTitle
            ? "bg-white before:opacity-100"
            : "bg-transparent before:opacity-0",
        )}
      >
        <div
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute inset-x-0 top-full h-8 bg-gradient-to-b from-white to-transparent transition-opacity duration-300 ease-out",
            isHistoryTitle ? "opacity-100" : "opacity-0",
          )}
        >
          <div className="size-full bg-gradient-to-b from-primary/10 to-transparent" />
        </div>
        {/* The dialog keeps "How can we help?" as its name; the swap to
            "Chat history" is visual only. */}
        <h2
          id={titleId}
          className="mb-0 grid min-w-px flex-1 text-2xl font-bold text-black"
        >
          <span
            className={cn(swap, isHistoryTitle && "-translate-y-2 opacity-0")}
          >
            {t("chat_how_can_we_help")}
          </span>
          <span
            aria-hidden="true"
            // The size of the list's "Chat history" heading; the header
            // keeps its height, so the list never jumps.
            className={cn(
              swap,
              "self-center truncate text-lg font-bold",
              !isHistoryTitle && "translate-y-2 opacity-0",
            )}
          >
            {t("chat_history")}
          </span>
        </h2>
        {/* The icon buttons sit 4px apart, as in the other headers. */}
        <div className="flex shrink-0 items-center gap-1">
          <ChatRefreshButton onRefresh={onRefresh} />
          <ChatCloseButton onClose={onClose} />
        </div>
      </div>

      <div
        ref={scrollRef}
        onScroll={event => {
          scrollMemory.set(event.currentTarget.scrollTop);
        }}
        className="relative flex min-h-px flex-1 flex-col gap-5 overflow-y-auto overscroll-contain px-4 pb-4"
      >
        <div className="flex w-full shrink-0 flex-col items-start gap-3 text-clip rounded-2xl bg-gradient-to-br from-[#F97C50] to-[#FCBC65] p-4 shadow-sm">
          <div className="flex w-full flex-col gap-0.5 leading-normal text-white">
            <p className="mb-0 text-lg font-bold">{t("chat_got_a_question")}</p>
            <p className="mb-0 text-sm">{t("chat_ask_about")}</p>
          </div>
          <button
            type="button"
            onClick={() => {
              onStart();
            }}
            disabled={isStarting}
            className="flex items-center gap-2 whitespace-nowrap rounded-full bg-white px-5 py-2.5 text-sm font-bold text-primary-700 transition hover:bg-white/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 active:scale-95 disabled:opacity-60"
          >
            {t("chat_send_a_message")}
            <LuSendHorizontal
              className="size-5 shrink-0 rtl:-scale-x-100"
              aria-hidden="true"
            />
          </button>
        </div>

        {hasHistory ? (
          <div className="flex w-full shrink-0 flex-col gap-2">
            <h3 ref={historyRef} className="mb-0 text-lg font-bold text-black">
              {t("chat_history")}
            </h3>
            <ul className="m-0 flex w-full list-none flex-col gap-2 p-0">
              {conversations.map(conversation => (
                <li key={String(conversation.id)} className="m-0 p-0">
                  <ChatCard
                    conversation={conversation}
                    onOpen={() => {
                      onOpen(String(conversation.id));
                    }}
                  />
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="flex w-full shrink-0 flex-col gap-2">
            <h3 className="mb-0 text-lg font-bold text-black">
              {t("chat_popular_topics")}
            </h3>
            <ul className="m-0 flex w-full list-none flex-col divide-y divide-gray-100 text-clip rounded-2xl border border-gray-200 bg-white p-0">
              {CHAT_TOPICS.map(topic => {
                const TopicIcon: IconType | undefined = TOPIC_ICONS[topic.icon];
                return (
                  <li key={topic.icon} className="m-0 p-0">
                    <button
                      type="button"
                      onClick={() => {
                        onStart(topic.key);
                      }}
                      disabled={isStarting}
                      className="group flex w-full items-center gap-2 bg-white px-3 py-4 text-start transition-colors duration-150 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-200 disabled:opacity-60"
                    >
                      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary-700 transition-colors duration-150 ease-out group-hover:bg-white">
                        {TopicIcon !== undefined && (
                          <TopicIcon className="size-4" aria-hidden="true" />
                        )}
                      </span>
                      <span className="min-w-px flex-1 text-sm font-semibold text-black">
                        {t(topic.key)}
                      </span>
                      <LuChevronRight
                        className="size-4 shrink-0 text-black/50 transition-transform duration-150 ease-out motion-safe:group-hover:translate-x-0.5 rtl:-scale-x-100 motion-safe:rtl:group-hover:-translate-x-0.5"
                        aria-hidden="true"
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </>
  );
};

export default ChatHome;
